import {useEffect, useMemo, useState} from "react";
import {useForm, Controller, useWatch} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, Row} from "antd";
import Button from "~/components/Button";
import Loading from "~/components/Loading";
import {
	InputField,
	InputPriceField,
	GroupRadioField,
	SelectContractAll,
	CheckBoxField,
	DateField,
	SelectField
} from "~/components/Forms";
import {useUtilities} from "~/hooks";
import {contractApi} from "~/api";
import {handleRequest} from "~/utils";

const FEE_TYPE_OPTIONS = [
	{value: 'service', label: 'Phí dịch vụ'},
	{value: 'budget', label: 'Ngân sách quảng cáo'},
];

function RevenueFormAddEdit({item, onHandleSubmit, can, fixedContract}) {

	const isEdit = Boolean(item?.id);

	// Khi mở từ màn hợp đồng: hợp đồng đã biết trước, khoá không cho chọn lại
	const isFixed = Boolean(fixedContract?.id);

	// Doanh thu chính có phí thẻ: cardFeePercent > 0 và không phải dòng phí thẻ (cardFeeRef = 0)
	const isCardFeeMain = isEdit && item?.cardFeePercent > 0 && !item?.cardFeeRef;

	const {utilities} = useUtilities();

	const bankAccountOptions = useMemo(() => {
		return Object.entries(utilities.bankAccounts || {}).map(([key, value]) => ({
			value: key,
			label: value.title,
		}));
	}, [utilities.bankAccounts]);

	const contractOptions = (isEdit && typeof item.contract?.id != "undefined")
		? [{ label : `HD${item.contract.id} ${item.contract.name}`, value : item.contract.id }]
		: (isFixed ? [{ label : `HD${fixedContract.id} ${fixedContract.name || ''}`, value : fixedContract.id }] : []);

	const paymentOptions = [
		{ value: 'cod', label: 'Tiền mặc'},
		{ value: 'bank', label: 'Chuyển khoản'},
		{ value: 'momo', label: 'Ví Momo'},
		{ value: 'swipe', label: 'Pos'},
	];

	const cardFeeOptions = [
		{ value: 'card', label: 'Quẹt thẻ (1.8%)'},
		{ value: 'link', label: 'Liên kết (2.5%)'},
	];

	let roomSales = [];

	if(can.plus)
	{
		roomSales = [
			{ value: 0, label: 'Không tính cho phòng'},
			{ value: 1, label: 'Tính cho phòng'},
			{ value: 2, label: 'Không tinh doanh số'},
		];
	}
	else {
		roomSales = [
			{ value: 2, label: 'Không tinh doanh số'},
		];
	}

	const initialValues = {
		contractId: fixedContract?.id || '',
		time: new Date(),
		price: 0,
		feeType: 'service',
		payment: 'cod',
		bankAccount: '',
		cardFeeType: 'card',
		cardFeePercent: '',
	}

	const validationSchema = Yup.object().shape({
		contractId: Yup.string().required('Bạn chưa chọn hợp đồng'),
		time: Yup.string().required('Bạn chưa chọn ngày thu'),
		price: Yup.number().required('Giá trị thu không được để trống'),
		payment: Yup.string().required('Bạn chưa chọn phương thức thanh toán'),
		cardFeeType: Yup.string().when('payment', {
			is: 'swipe',
			then: (schema) => schema.required('Bạn chưa chọn loại phí thẻ'),
		}),
	})

	const { control, handleSubmit, formState: { isSubmitting, errors }, reset } = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema)
	});

	const payment = useWatch({ control, name: 'payment' });

	const contractId = useWatch({ control, name: 'contractId' });

	//Ngân sách quảng cáo của hợp đồng (nếu là HĐ marketing có ngân sách) -> cho phép chọn loại phí
	const [contractBudget, setContractBudget] = useState(item?.contract?.budget || fixedContract?.budget || 0);

	useEffect(() => {
		let active = true;
		if(!contractId) { setContractBudget(0); return; }
		(async () => {
			const [error, response] = await handleRequest(contractApi.feeInfo(contractId));
			if(active && !error && response) setContractBudget(response.data?.budget || 0);
		})();
		return () => { active = false; };
	}, [contractId]);

	useEffect(() => {
		if(item?.id) {
			initialValues.contractId = item.contractId;
			initialValues.time = item.time*1000;
			initialValues.price = item.price;
			initialValues.feeType = item.feeType || 'service';
			initialValues.payment = item.payment;
			initialValues.bankAccount = item.bankAccount || '';
			if(item.cardFeePercent > 0 && !item.cardFeeRef) {
				// doanh thu chính: hiển thị tổng (gồm phí thẻ) trong ô giá trị thu
				initialValues.price = item.price + (item.cardFeePrice || 0);
				initialValues.cardFeePercent = item.cardFeePercent;
			}
			if(!can.plus)
			{
				initialValues.roomSales1 = [item.roomSales];
			}
			else
			{
				initialValues.roomSales = item.roomSales;
			}
		}
		reset(initialValues);
	}, [item]);

	const onSubmit = async (data) => {
		// chỉ gửi % phí thẻ khi sửa doanh thu chính có phí thẻ, tránh lỗi validate ở các trường hợp khác
		if(!isCardFeeMain || data.cardFeePercent === '' || data.cardFeePercent === null || typeof data.cardFeePercent === 'undefined') {
			delete data.cardFeePercent;
		}
		await onHandleSubmit(data, isEdit);
	}

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)}>
			{isSubmitting && <Loading/>}
			<p className="heading">Liên quan hợp đồng</p>
			{isFixed ? (
				<Controller control={control} name="contractId" render={() => (
					<div className="form-group">
						<label>Hợp đồng</label>
						<div className="form-control-static">{`HD${fixedContract.id} ${fixedContract.name || ''}`}</div>
					</div>
				)}/>
			) : (
				<Controller control={control} name="contractId" render={({ field }) => (
					<SelectContractAll label="Hợp đồng" options={contractOptions} errors={errors} {...field} />
				)}/>
			)}
			<p className="heading">Thông tin doanh thu</p>
			{contractBudget > 0 && (
				<Controller control={control} name="feeType" render={({ field }) => (
					<GroupRadioField label="Loại phí thu" options={FEE_TYPE_OPTIONS} errors={errors} {...field}/>
				)}/>
			)}
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="price" render={({field}) => (
						<InputPriceField label={isCardFeeMain ? "Giá trị thu (gồm phí thẻ)" : "Giá trị thu"} errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="time" render={({ field }) => (
						<DateField label="Ngày thu" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			{isCardFeeMain && (
				<Controller control={control} name="cardFeePercent" render={({field}) => (
					<InputField
						label="% phí thẻ"
						type="number"
						step="0.01"
						suffix="%"
						disabled={!can?.cardFee}
						errors={errors}
						{...field}
					/>
				)}/>
			)}
			<p className="heading">Thanh toán</p>
			{
				can.plus && <Controller control={control} name="roomSales" render={({ field }) => (
					<GroupRadioField label="Doanh số" options={roomSales} errors={errors} {...field}/>
				)}/>
			}
			{
				!can.plus && <Controller control={control} name="roomSales1" render={({ field }) => (
					<CheckBoxField label="Doanh số" errors={errors} {...field}>Không tính doanh số</CheckBoxField>
				)}/>
			}

			<Controller control={control} name="payment" render={({ field }) => (
				<GroupRadioField label="Hình thức thanh toán" options={paymentOptions} errors={errors} {...field}/>
			)}/>
			{payment === 'bank' && (
				<Controller control={control} name="bankAccount" render={({ field }) => (
					<SelectField label="Tài khoản ngân hàng" options={bankAccountOptions} errors={errors} {...field}/>
				)}/>
			)}
			{payment === 'swipe' && !isEdit && (
				<Controller control={control} name="cardFeeType" render={({ field }) => (
					<GroupRadioField label="Loại phí thẻ" options={cardFeeOptions} errors={errors} {...field}/>
				)}/>
			)}
			<hr />
			<div className="form-group d-flex justify-content-end modal-bottom">
				<Button primary type="submit">{isEdit ? 'Cập nhật' : 'Thêm'} doanh thu</Button>
			</div>
		</form>
	)
}

export default RevenueFormAddEdit;